import {
  ChatArchiveIcon,
  ChatBackIcon,
  ChatInfoIcon,
  ChatRestoreIcon,
  ConversationListItemResponse,
  Div,
  GroupMembersTooltip,
  isGroupConversation,
  lightTheme,
  P,
} from '@vgrubs/components';
import { ReactNode } from 'react';
import { HeaderActionSlot } from './Chat.styled';
import { openUserDetails } from './userDetailsPath';

/**
 * The bar inside the open conversation: who it is with, and the way into its
 * details.
 *
 * Rendered into `ChatThread`'s own header slot rather than above it, so the
 * name, the messages and the composer share one surface.
 */

/** Icon plus a label that hides itself on a phone, where the icon is enough. */
const HeaderAction = ({
  icon,
  label,
  onClick,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) => (
  <HeaderActionSlot onClick={onClick}>
    {icon}
    <Div $desktop>
      <P $m="0" $bold>
        {label}
      </P>
    </Div>
  </HeaderActionSlot>
);

export const ChatThreadHeader = ({
  thread,
  fallbackName,
  memberNames,
  archived,
  onBack,
  onOpenDetails,
  onToggleArchive,
}: {
  thread?: ConversationListItemResponse;
  /**
   * Name for a thread the inbox has no row for - a conversation with no
   * messages in it is not in the list, and one can be started at any moment.
   */
  fallbackName?: string;
  /** Group members, for the tooltip. Absent for the other two types. */
  memberNames?: string[];
  /** Drives the label and the icon: the same control archives and restores. */
  archived?: boolean;
  onBack: () => void;
  onOpenDetails: () => void;
  /** Opens the confirmation; the page owns the request itself. */
  onToggleArchive: () => void;
}) => {
  const isGroup = isGroupConversation(thread?.type);

  /*
   * The member count belongs to the group's name, not beside it - "Dev team
   * (5)" is what the group is called here. Held in the same element so the two
   * underline together and the whole thing is one target.
   */
  const title =
    isGroup && thread
      ? `${thread.name} (${thread.participantCount})`
      : thread?.name || fallbackName;

  /*
   * What the title leads to, which is a different thing per type:
   *
   *   - support - the person on the other end, whose id is right here.
   *   - group   - its details, where the members and the name live.
   *   - direct  - nothing. The other member IS reachable from Details, but
   *               the name alone does not say that is where it goes.
   */
  const openTitle = thread?.supportUserId
    ? () =>
        openUserDetails(thread.supportUserId as number, thread.supportUserRole)
    : isGroup
    ? onOpenDetails
    : undefined;

  return (
    <>
      <Div $flex $alignCenter $gap="12px" style={{ minWidth: 0 }}>
        {/* Mobile only - on desktop the inbox is right there beside the
            thread, so there is nothing to go back to. */}
        <Div $mobile>
          <HeaderActionSlot
            role="button"
            aria-label="Back to conversations"
            onClick={onBack}
          >
            <ChatBackIcon />
          </HeaderActionSlot>
        </Div>

        <GroupMembersTooltip
          // One header on screen at a time, so a constant id is stable enough.
          id="chat-group-members"
          names={isGroup ? memberNames : undefined}
        >
          <P
            $m="0"
            $bold
            $pointer={!!openTitle}
            $hoverUnderline={!!openTitle}
            onClick={openTitle}
          >
            {title}
          </P>
        </GroupMembersTooltip>
      </Div>

      <Div $flex $alignCenter $gap="16px" $mr="10">
        {/* One control for both directions: the shelf is a place a thread is
            in or out of, and a separate Restore button would sit greyed out
            over every conversation that is not on it. */}
        <HeaderAction
          icon={
            archived ? (
              <ChatRestoreIcon color={lightTheme.darkGray} />
            ) : (
              <ChatArchiveIcon color={lightTheme.darkGray} />
            )
          }
          label={archived ? 'Restore' : 'Archive'}
          onClick={onToggleArchive}
        />

        {/* Details covers members, rename, photo, mute and leave. Offered for
            every type - mute belongs to all three - while the group-only
            controls are gated inside it. */}
        <HeaderAction
          icon={<ChatInfoIcon color={lightTheme.darkGray} />}
          label="Details"
          onClick={onOpenDetails}
        />
      </Div>
    </>
  );
};
